import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Package, TrendingUp, TrendingDown, ArrowUpDown, Store, Clock, IndianRupee, BarChart3, ShoppingCart, AlertTriangle, Download, RefreshCw, Zap, Settings, Tag, Search, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── INPUT TAB ──────────────────────────────────────────────
function InputTab({ onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sales-analytics/status`).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) {
      toast.error("Only .xlsx files are supported");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/sales-analytics/upload`, form);
      setResult(r.data);
      toast.success(`Uploaded: ${r.data.stock?.new || 0} new stock + ${r.data.sales?.new || 0} new sales records`);
      onUploadSuccess?.();
      axios.get(`${API}/sales-analytics/status`).then(r => setStatus(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-slate-400 transition-colors bg-white">
        <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-400" />
        <p className="text-sm text-slate-600 mb-1">Upload Excel file with STOCK and SALES sheets</p>
        <p className="text-xs text-slate-400 mb-4">Duplicate entries are automatically skipped</p>
        <Button disabled={uploading} data-testid="upload-excel-btn" variant="default" size="sm"
          onClick={() => document.getElementById("sales-excel-input").click()}>
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "Processing..." : "Choose File"}
        </Button>
        <input id="sales-excel-input" type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
      </div>

      {/* Upload Result */}
      {result && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="upload-result">
          <h3 className="font-semibold text-slate-800 text-sm">Upload Summary — {result.filename}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-medium">Stock Data</p>
              <p className="text-lg font-bold text-blue-800">{result.stock?.new || 0} <span className="text-xs font-normal">new</span></p>
              <p className="text-xs text-blue-500">{result.stock?.skipped || 0} duplicates skipped</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-emerald-600 font-medium">Sales Data</p>
              <p className="text-lg font-bold text-emerald-800">{result.sales?.new || 0} <span className="text-xs font-normal">new</span></p>
              <p className="text-xs text-emerald-500">{result.sales?.skipped || 0} duplicates skipped</p>
            </div>
          </div>
          <p className="text-xs text-slate-400">Sheets found: {result.sheets_found?.join(", ")}</p>
        </div>
      )}

      {/* Current Data Status */}
      {status && (
        <div className="bg-white rounded-xl border p-5" data-testid="data-status">
          <h3 className="font-semibold text-slate-800 text-sm mb-3">Current Data Status</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">{status.stock_records?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Stock records</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">{status.sales_records?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Sales records</p>
              </div>
            </div>
          </div>
          {status.last_upload && (
            <p className="text-xs text-slate-400 mt-3">
              Last upload: {status.last_upload.filename} — {new Date(status.last_upload.uploaded_at).toLocaleString()}
            </p>
          )}
          <div className="mt-4 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/sales-analytics/export`, "_blank")} data-testid="export-data-btn">
              <Download className="w-4 h-4 mr-2" />Export All Data (Excel)
            </Button>
            <span className="text-xs text-slate-400 ml-3">Downloads STOCK + SALES sheets in the same format as uploaded</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STOCK POSITION TAB ─────────────────────────────────────
function StockPositionTab() {
  const [summary, setSummary] = useState(null);
  const [slowMovers, setSlowMovers] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("total_value");
  const [sortDir, setSortDir] = useState(-1);
  const [selectedStore, setSelectedStore] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s] = await Promise.all([
        axios.get(`${API}/sales-analytics/stock/summary`),
      ]);
      setSummary(s.data);
      // Load unfiltered slow movers and stock by product
      const [slow, prod] = await Promise.all([
        axios.get(`${API}/sales-analytics/stock/slow-movers?min_age=60`),
        axios.get(`${API}/sales-analytics/stock/by-product`),
      ]);
      setSlowMovers(slow.data);
      setByProduct(prod.data);
    } catch { toast.error("Failed to load stock data"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reload slow movers + stock by product when store selection changes
  useEffect(() => {
    const storeParam = selectedStore ? `&store=${encodeURIComponent(selectedStore)}` : "";
    Promise.all([
      axios.get(`${API}/sales-analytics/stock/slow-movers?min_age=60${storeParam}`),
      axios.get(`${API}/sales-analytics/stock/by-product${selectedStore ? `?store=${encodeURIComponent(selectedStore)}` : ""}`),
    ]).then(([slow, prod]) => {
      setSlowMovers(slow.data);
      setByProduct(prod.data);
    }).catch(() => {});
  }, [selectedStore]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div></div>;
  if (!summary || summary.total_units === 0) return <div className="text-center py-12 text-slate-400">No stock data. Upload an Excel file first.</div>;

  const sortedStores = [...(summary.stores || [])].sort((a, b) => (a[sortField] - b[sortField]) * sortDir);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Package} label="Total Stock" value={summary.total_units.toLocaleString()} sub="units" color="blue" />
        <KPI icon={IndianRupee} label="Stock Value" value={`${(summary.total_value / 100000).toFixed(1)}L`} sub={`Rs.${summary.total_value.toLocaleString()}`} color="emerald" />
        <KPI icon={Store} label="Stores" value={summary.store_count} sub="active" color="purple" />
        <KPI icon={AlertTriangle} label="Slow Movers" value={slowMovers.length} sub={selectedStore || "> 60 days old"} color="amber" />
      </div>

      {/* Store-wise Stock */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b flex justify-between items-center">
          <h3 className="font-semibold text-slate-800 text-sm">Stock Position by Store</h3>
          {selectedStore && (
            <button onClick={() => setSelectedStore(null)} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="clear-store-filter">
              Clear filter: {selectedStore}
            </button>
          )}
        </div>
        <p className="px-5 py-1.5 text-xs text-slate-400">Click a store to filter slow movers and stock by product below</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stock-store-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2.5 text-left">Store</th>
                <th className="px-4 py-2.5 text-left">Region</th>
                <th className="px-4 py-2.5 text-right cursor-pointer" onClick={() => { setSortField("total_units"); setSortDir(d => -d); }}>
                  Units <ArrowUpDown className="w-3 h-3 inline ml-1" />
                </th>
                <th className="px-4 py-2.5 text-right cursor-pointer" onClick={() => { setSortField("total_value"); setSortDir(d => -d); }}>
                  Value <ArrowUpDown className="w-3 h-3 inline ml-1" />
                </th>
                <th className="px-4 py-2.5 text-right">Products</th>
                <th className="px-4 py-2.5 text-right cursor-pointer" onClick={() => { setSortField("avg_age"); setSortDir(d => -d); }}>
                  Avg Age <ArrowUpDown className="w-3 h-3 inline ml-1" />
                </th>
                <th className="px-4 py-2.5 text-right">Max Age</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedStores.map((s, i) => (
                <tr
                  key={i}
                  className={`cursor-pointer transition-colors ${selectedStore === s.store ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"}`}
                  onClick={() => setSelectedStore(prev => prev === s.store ? null : s.store)}
                  data-testid={`store-row-${i}`}
                >
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    {selectedStore === s.store && <span className="text-blue-500 mr-1">&#9654;</span>}
                    {s.store}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{s.region}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{s.total_units}</td>
                  <td className="px-4 py-2.5 text-right">Rs.{s.total_value.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right">{s.product_count}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={s.avg_age > 90 ? "text-red-600 font-medium" : s.avg_age > 60 ? "text-amber-600" : "text-emerald-600"}>
                      {s.avg_age}d
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{s.max_age}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slow Movers & Stock by Product */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Slow Movers */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-amber-50">
            <h3 className="font-semibold text-amber-800 text-sm flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Slow Movers (60+ days){selectedStore ? ` — ${selectedStore}` : ""}
            </h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {slowMovers.length === 0 ? (
              <p className="text-center py-6 text-slate-400 text-sm">No slow movers{selectedStore ? ` in ${selectedStore}` : ""}</p>
            ) : (
            <table className="w-full text-xs" data-testid="slow-movers-table">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  {!selectedStore && <th className="px-3 py-2 text-left">Store</th>}
                  <th className="px-3 py-2 text-right">Age</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {slowMovers.slice(0, 50).map((item, i) => (
                  <tr key={i} className="hover:bg-amber-50/50">
                    <td className="px-3 py-1.5 font-medium text-slate-700">{item.product}</td>
                    {!selectedStore && <td className="px-3 py-1.5 text-slate-500">{item.store}</td>}
                    <td className="px-3 py-1.5 text-right">
                      <span className={item.max_age > 120 ? "text-red-600 font-bold" : "text-amber-600 font-medium"}>
                        {item.max_age}d
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">{item.units}</td>
                    <td className="px-3 py-1.5 text-right text-slate-500">Rs.{item.total_value?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>

        {/* Stock by Product */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-blue-50">
            <h3 className="font-semibold text-blue-800 text-sm flex items-center gap-2">
              <Package className="w-4 h-4" /> Stock by Product{selectedStore ? ` — ${selectedStore}` : " (All Stores)"}
            </h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="stock-product-table">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Units</th>
                  <th className="px-3 py-2 text-right">Value</th>
                  <th className="px-3 py-2 text-right">Avg Age</th>
                  {!selectedStore && <th className="px-3 py-2 text-right">Stores</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {byProduct.map((p, i) => (
                  <tr key={i} className="hover:bg-blue-50/50">
                    <td className="px-3 py-1.5 font-medium text-slate-700">{p.product}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{p.total_units}</td>
                    <td className="px-3 py-1.5 text-right">Rs.{p.total_value.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={p.avg_age > 90 ? "text-red-600" : p.avg_age > 60 ? "text-amber-600" : "text-emerald-600"}>
                        {p.avg_age}d
                      </span>
                    </td>
                    {!selectedStore && <td className="px-3 py-1.5 text-right">{p.store_count}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SALES POSITION TAB ─────────────────────────────────────
function SalesPositionTab() {
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [storeProducts, setStoreProducts] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all"); // all | month | quarter | year
  const [filterValue, setFilterValue] = useState("");
  const [periodsData, setPeriodsData] = useState({ months: [], quarters: [], years: [] });

  // Load periods once on mount
  useEffect(() => {
    axios.get(`${API}/sales-analytics/sales/periods`)
      .then(r => setPeriodsData(r.data)).catch(() => {});
  }, []);

  // Compute date_from/date_to from filter
  const getDateParams = useCallback(() => {
    if (filterType === "all" || !filterValue) return "";
    let date_from, date_to;
    if (filterType === "month") {
      const [y, m] = filterValue.split("-");
      const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
      date_from = `${filterValue}-01`;
      date_to = `${filterValue}-${lastDay}`;
    } else if (filterType === "quarter") {
      const q = periodsData.quarters.find(q => q.label === filterValue);
      if (!q) return "";
      date_from = q.from;
      date_to = q.to;
    } else if (filterType === "year") {
      date_from = `${filterValue}-01-01`;
      date_to = `${filterValue}-12-31`;
    }
    if (!date_from) return "";
    return `date_from=${date_from}&date_to=${date_to}`;
  }, [filterType, filterValue, periodsData.quarters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const dp = getDateParams();
      const qs = dp ? `?${dp}` : "";
      const topQs = dp ? `?${dp}&limit=20` : "?limit=20";

      const [s, top, mo] = await Promise.all([
        axios.get(`${API}/sales-analytics/sales/summary${qs}`),
        axios.get(`${API}/sales-analytics/sales/top-products${topQs}`),
        axios.get(`${API}/sales-analytics/sales/monthly${qs}`),
      ]);
      setSummary(s.data);
      setTopProducts(top.data);
      setMonthly(mo.data);
    } catch { toast.error("Failed to load sales data"); }
    setLoading(false);
  }, [getDateParams]);

  useEffect(() => { loadData(); }, [loadData]);

  // Store drill-down
  useEffect(() => {
    if (!selectedStore) return;
    const dp = getDateParams();
    const storeParam = `store=${encodeURIComponent(selectedStore)}`;
    const qs = dp ? `?${storeParam}&${dp}` : `?${storeParam}`;
    axios.get(`${API}/sales-analytics/sales/by-store${qs}`)
      .then(r => setStoreProducts(r.data)).catch(() => {});
  }, [selectedStore, getDateParams]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div></div>;
  if (!summary || summary.total_quantity === 0) return <div className="text-center py-12 text-slate-400">No sales data{filterType !== "all" ? " for this period" : ""}. {filterType !== "all" ? "Try a different filter." : "Upload an Excel file first."}</div>;

  const monthNames = { "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun", "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec" };
  const maxMonthlyQty = Math.max(...monthly.map(m => m.total_qty), 1);

  const formatMonthLabel = (m) => {
    if (!m) return "";
    const parts = m.split("-");
    return `${monthNames[parts[1]] || parts[1]} ${parts[0]?.slice(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Date Filter Bar */}
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3" data-testid="date-filter-bar">
        <span className="text-xs font-semibold text-slate-500 uppercase">Period:</span>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {[
            { id: "all", label: "All Time" },
            { id: "month", label: "Month" },
            { id: "quarter", label: "Quarter" },
            { id: "year", label: "Year" },
          ].map(t => (
            <button
              key={t.id}
              data-testid={`filter-${t.id}`}
              onClick={() => { setFilterType(t.id); setFilterValue(""); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterType === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {filterType === "month" && periodsData.months.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {periodsData.months.map(m => (
              <button
                key={m}
                data-testid={`month-${m}`}
                onClick={() => setFilterValue(m)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  filterValue === m ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {formatMonthLabel(m)}
              </button>
            ))}
          </div>
        )}
        {filterType === "quarter" && periodsData.quarters.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {periodsData.quarters.map(q => (
              <button
                key={q.label}
                data-testid={`quarter-${q.label}`}
                onClick={() => setFilterValue(q.label)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  filterValue === q.label ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>
        )}
        {filterType === "year" && periodsData.years.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {periodsData.years.map(y => (
              <button
                key={y}
                data-testid={`year-${y}`}
                onClick={() => setFilterValue(y)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  filterValue === y ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={ShoppingCart} label="Total Sold" value={summary.total_quantity.toLocaleString()} sub="units" color="emerald" />
        <KPI icon={IndianRupee} label="Revenue" value={`${(summary.total_revenue / 100000).toFixed(1)}L`} sub={`Rs.${summary.total_revenue.toLocaleString()}`} color="blue" />
        <KPI icon={Store} label="Stores" value={summary.store_count} sub="selling" color="purple" />
        <KPI icon={TrendingUp} label="Top Product" value={topProducts[0]?.total_qty || 0} sub={topProducts[0]?.product || "-"} color="amber" />
      </div>

      {/* Monthly Trend */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-800 text-sm mb-4">Monthly Sales Trend</h3>
        <div className="flex items-end gap-3 h-40" data-testid="monthly-chart">
          {monthly.map((m, i) => {
            const height = (m.total_qty / maxMonthlyQty) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-bold text-slate-700">{m.total_qty}</span>
                <div className="w-full bg-blue-500 rounded-t-md transition-all" style={{ height: `${Math.max(height, 4)}%` }}></div>
                <span className="text-xs text-slate-500">{formatMonthLabel(m.month)}</span>
                <span className="text-xs text-slate-400">Rs.{(m.total_revenue / 1000).toFixed(0)}k</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Selling Products */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b bg-emerald-50">
            <h3 className="font-semibold text-emerald-800 text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Top Selling Products
            </h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="top-products-table">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Stores</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {topProducts.map((p, i) => (
                  <tr key={i} className="hover:bg-emerald-50/50">
                    <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-1.5 font-medium text-slate-700">{p.product}</td>
                    <td className="px-3 py-1.5 text-right font-bold text-emerald-700">{p.total_qty}</td>
                    <td className="px-3 py-1.5 text-right">Rs.{p.total_revenue.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">{p.store_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sales by Store */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b">
            <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
              <Store className="w-4 h-4" /> Sales by Store
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Click a store to see product breakdown</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="sales-store-table">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Store</th>
                  <th className="px-3 py-2 text-left">Region</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {summary.stores.map((s, i) => (
                  <tr
                    key={i}
                    className={`cursor-pointer transition-colors ${selectedStore === s.store ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"}`}
                    onClick={() => setSelectedStore(prev => prev === s.store ? "" : s.store)}
                  >
                    <td className="px-3 py-1.5 font-medium text-slate-700">
                      {selectedStore === s.store && <span className="text-blue-500 mr-1">&#9654;</span>}
                      {s.store}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500">{s.region}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{s.total_quantity}</td>
                    <td className="px-3 py-1.5 text-right">Rs.{s.total_revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Store Drill-down */}
      {selectedStore && storeProducts.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold text-slate-800 text-sm">Product Sales — {selectedStore}</h3>
            <button className="text-xs text-blue-600 hover:text-blue-800 font-medium" onClick={() => setSelectedStore("")}>Close</button>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Qty Sold</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">First Sale</th>
                  <th className="px-3 py-2 text-right">Last Sale</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {storeProducts.map((p, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 font-medium text-slate-700">{p.product}</td>
                    <td className="px-3 py-1.5 text-right font-bold">{p.total_qty}</td>
                    <td className="px-3 py-1.5 text-right">Rs.{p.total_revenue.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right text-slate-400">{p.first_sale}</td>
                    <td className="px-3 py-1.5 text-right text-slate-400">{p.last_sale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KPI CARD ───────────────────────────────────────────────
function KPI({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-xs text-slate-400 truncate max-w-[120px]">{sub}</p>}
      </div>
    </div>
  );
}

// ─── RE-ORDER ANALYTICS TAB ────────────────────────────────
function ReorderAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sales-analytics/reorder`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load re-order analytics"))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/sales-analytics/reorder/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      const now = new Date();
      const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      a.href = url;
      a.download = `Re-order ${months[now.getMonth()]} ${now.getFullYear()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Re-order file downloaded");
    } catch {
      toast.error("Download failed");
    }
    setDownloading(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div></div>;
  if (!data) return <div className="text-center py-12 text-slate-400">No data available.</div>;

  const { urgent, restock, skipped, summary } = data;

  // Combine all reorder items and group by store
  const allReorders = [...urgent, ...restock];
  const storeMap = {};
  allReorders.forEach(r => {
    if (!storeMap[r.store]) storeMap[r.store] = { urgent: 0, restock: 0, items: [] };
    storeMap[r.store].items.push(r);
    if (r.priority === "URGENT") storeMap[r.store].urgent++;
    else storeMap[r.store].restock++;
  });
  const storeNames = Object.keys(storeMap).sort();

  const storeItems = selectedStore ? (storeMap[selectedStore]?.items || []) : [];

  return (
    <div className="space-y-5">
      {/* Header with Download */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-slate-500">Click a store to view items that need re-ordering</p>
        </div>
        <Button onClick={handleDownload} disabled={downloading} data-testid="download-reorder-btn" variant="default" size="sm">
          <Download className="w-4 h-4 mr-2" />
          {downloading ? "Generating..." : "Download Re-order Excel"}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Zap} label="Urgent Re-orders" value={summary.total_urgent} sub={`Rs.${(summary.urgent_value / 100000).toFixed(1)}L MRP`} color="amber" />
        <KPI icon={RefreshCw} label="Restock Needed" value={summary.total_restock} sub={`Rs.${(summary.restock_value / 100000).toFixed(1)}L MRP`} color="blue" />
        <KPI icon={AlertTriangle} label="No HQ Stock" value={summary.total_skipped_no_hq} sub="Cannot fulfill" color="purple" />
        <KPI icon={Package} label="SKUs Mapped" value={`${summary.total_products_mapped}/${summary.total_inventory_skus}`} sub="inventory matched" color="emerald" />
      </div>

      {/* Store Buttons */}
      <div className="bg-white rounded-xl border p-4" data-testid="reorder-store-buttons">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Select Store</h3>
        <div className="flex flex-wrap gap-2">
          {storeNames.map(store => {
            const info = storeMap[store];
            const isSelected = selectedStore === store;
            return (
              <button
                key={store}
                data-testid={`reorder-store-${store.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setSelectedStore(prev => prev === store ? null : store)}
                className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                  isSelected
                    ? "bg-slate-800 text-white border-slate-800 shadow-md"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:shadow-sm"
                }`}
              >
                <span>{store}</span>
                <span className="ml-2 opacity-75">
                  {info.urgent > 0 && <span className={isSelected ? "text-red-300" : "text-red-500"}>{info.urgent}U</span>}
                  {info.urgent > 0 && info.restock > 0 && " · "}
                  {info.restock > 0 && <span className={isSelected ? "text-blue-300" : "text-blue-500"}>{info.restock}R</span>}
                </span>
              </button>
            );
          })}
          {storeNames.length === 0 && (
            <p className="text-sm text-slate-400">No stores with re-order needs</p>
          )}
        </div>
      </div>

      {/* Store Items Table */}
      {selectedStore && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="reorder-store-detail">
          <div className="px-5 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold text-slate-800 text-sm">
              Re-orders for {selectedStore}
              <span className="ml-2 text-xs font-normal text-slate-500">({storeItems.length} items)</span>
            </h3>
            <button onClick={() => setSelectedStore(null)} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="clear-reorder-store">
              Close
            </button>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="reorder-items-table">
              <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0 text-[11px]">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Current Stock</th>
                  <th className="px-3 py-2 text-right">Monthly Sales</th>
                  <th className="px-3 py-2 text-right">Suggested Re-order Qty</th>
                  <th className="px-3 py-2 text-right">MRP</th>
                  <th className="px-3 py-2 text-center">Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {storeItems.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-slate-700 font-medium">{r.product}</td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={r.current_stock === 0 ? "text-red-600 font-bold" : ""}>{r.current_stock}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium">{r.monthly_velocity}/mo</td>
                    <td className="px-3 py-1.5 text-right font-bold text-blue-700">{r.suggested_qty}</td>
                    <td className="px-3 py-1.5 text-right text-slate-500">Rs.{r.mrp?.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        r.priority === "URGENT" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                      }`}>
                        {r.priority}
                      </span>
                    </td>
                  </tr>
                ))}
                {storeItems.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-400">No items for this store</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 space-y-1">
        <p><span className="font-semibold text-red-600">Urgent (U):</span> Store stock will run out within 30 days based on current sales velocity.</p>
        <p><span className="font-semibold text-blue-600">Restock (R):</span> Out of stock or stock will run out within 60 days.</p>
        <p><span className="font-semibold text-slate-700">Suggested Qty:</span> Calculated for ~2 months of sales, capped at available HQ stock.</p>
      </div>
    </div>
  );
}


// ─── REORDER LOGIC TAB ─────────────────────────────────────
function ReorderLogicTab() {
  const [settings, setSettings] = useState(null);
  const [stockMonths, setStockMonths] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sales-analytics/reorder/settings`)
      .then(r => {
        setSettings(r.data);
        setStockMonths(r.data.stock_months || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/sales-analytics/reorder/settings`, { stock_months: stockMonths });
      setSettings(r.data);
      toast.success("Reorder logic updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div></div>;

  const presets = [
    { label: "1 Month", value: 1, desc: "Match monthly consumption exactly" },
    { label: "1.5 Months", value: 1.5, desc: "50% buffer above monthly sales" },
    { label: "2 Months", value: 2, desc: "Double the monthly consumption" },
    { label: "3 Months", value: 3, desc: "Quarter stock, aggressive growth" },
  ];

  // Example calculations
  const examples = [
    { vel: 1, stock: 1 },
    { vel: 1.2, stock: 1 },
    { vel: 3, stock: 2 },
    { vel: 5.5, stock: 0 },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="text-sm text-slate-500">Configure how many months of stock each store should maintain. This determines the suggested re-order quantity for every SKU.</p>
      </div>

      {/* Current Setting */}
      <div className="bg-white rounded-xl border p-5 space-y-4">
        <h3 className="font-semibold text-slate-800 text-sm">Stock Months Multiplier</h3>
        <p className="text-xs text-slate-500">
          Formula: <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">Suggested Qty = max(ceil(Monthly Sales x Multiplier) - Current Stock, 0)</span>
        </p>

        {/* Presets */}
        <div className="flex flex-wrap gap-2" data-testid="reorder-presets">
          {presets.map(p => (
            <button
              key={p.value}
              data-testid={`preset-${p.value}`}
              onClick={() => setStockMonths(p.value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                stockMonths === p.value
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom Input */}
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-slate-600">Custom value:</label>
          <input
            type="number"
            data-testid="stock-months-input"
            min="0.5"
            max="12"
            step="0.5"
            value={stockMonths}
            onChange={e => setStockMonths(parseFloat(e.target.value) || 1)}
            className="w-24 px-3 py-1.5 border rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <span className="text-xs text-slate-400">months (0.5 — 12)</span>
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving} data-testid="save-reorder-settings" variant="default" size="sm">
            {saving ? "Saving..." : "Save Setting"}
          </Button>
          {settings?.updated_at && (
            <span className="text-xs text-slate-400">Last updated: {new Date(settings.updated_at).toLocaleString()}</span>
          )}
        </div>
      </div>

      {/* Live Preview */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b bg-slate-50">
          <h3 className="font-semibold text-slate-700 text-sm">Preview — {stockMonths}x Monthly Sales</h3>
        </div>
        <table className="w-full text-xs" data-testid="reorder-preview-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px]">
            <tr>
              <th className="px-4 py-2 text-right">Monthly Sales</th>
              <th className="px-4 py-2 text-right">Current Stock</th>
              <th className="px-4 py-2 text-right">Suggested Re-order</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {examples.map((ex, i) => {
              const suggested = Math.max(Math.ceil(ex.vel * stockMonths) - ex.stock, 0);
              return (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-right font-medium">{ex.vel}/mo</td>
                  <td className="px-4 py-2 text-right">{ex.stock}</td>
                  <td className="px-4 py-2 text-right font-bold text-blue-700">{suggested}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
        <p>Changes apply immediately to the Re-order Analytics tab and Excel downloads. Higher multipliers mean more stock in hand — useful during growth phases.</p>
      </div>
    </div>
  );
}


// ─── SKU ANALYTICS TAB ──────────────────────────────────────
function SKUAnalyticsTab({ apiBase }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("total_qty");
  const [sortDir, setSortDir] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSku, setExpandedSku] = useState(null);

  const loadData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const r = await axios.get(`${apiBase}/sku-analytics?${params}`);
      setData(r.data);
    } catch { toast.error("Failed to load SKU analytics"); }
    setLoading(false);
  }, [apiBase]);

  useEffect(() => { loadData(dateFrom, dateTo); }, [loadData, dateFrom, dateTo]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const filtered = useMemo(() => {
    if (!data?.skus) return [];
    let items = data.skus;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      items = items.filter(i => i.product.toLowerCase().includes(s));
    }
    items.sort((a, b) => {
      const av = a[sortBy] ?? 0, bv = b[sortBy] ?? 0;
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return items;
  }, [data, searchTerm, sortBy, sortDir]);

  const fmtINR = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const SortHead = ({ col, label, align }) => (
    <TableHead className={`cursor-pointer hover:text-slate-800 select-none ${align === "right" ? "text-right" : ""}`} onClick={() => handleSort(col)}>
      <span className="flex items-center gap-1 justify-start">
        {label}
        {sortBy === col && <span className="text-blue-500">{sortDir === "desc" ? "↓" : "↑"}</span>}
      </span>
    </TableHead>
  );

  const urgencyBadge = (u) => {
    if (u === "restock_now") return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">RESTOCK NOW</span>;
    if (u === "plan_restock") return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">PLAN RESTOCK</span>;
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">OK</span>;
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading SKU analytics...</div>;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-xs h-8" placeholder="From" />
            <span className="text-slate-400 text-xs">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border rounded-md px-2 py-1.5 text-xs h-8" placeholder="To" />
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-slate-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full border rounded-md pl-8 pr-3 py-1.5 text-xs h-8" placeholder="Search SKU..." />
          </div>
          <div className="text-xs text-slate-400">{filtered.length} SKUs{data?.months?.length > 0 && ` · ${data.months.length} months`}</div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total SKUs", value: filtered.length, color: "text-slate-800" },
          { label: "Total Units Sold", value: filtered.reduce((s, i) => s + i.total_qty, 0), color: "text-blue-700" },
          { label: "Total Revenue", value: fmtINR(filtered.reduce((s, i) => s + i.total_revenue, 0)), color: "text-emerald-700" },
          { label: "Need Restock", value: filtered.filter(i => i.restock_urgency !== "ok").length, color: "text-red-700" },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-xl border p-4">
            <p className="text-xs text-slate-500">{c.label}</p>
            <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* SKU Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-8"></TableHead>
                <SortHead col="product" label="SKU / Product" />
                <SortHead col="total_qty" label="Units Sold" />
                <SortHead col="total_revenue" label="Revenue" />
                <SortHead col="mrp" label="MRP" />
                <SortHead col="store_count" label="Stores" />
                <SortHead col="warehouse_stock" label="Our Stock" />
                <SortHead col="store_stock" label="Store Stock" />
                <SortHead col="daily_rate" label="Daily Rate" />
                <SortHead col="days_of_stock" label="Days Left" />
                <TableHead>Restock</TableHead>
                <SortHead col="suggested_reorder" label="Reorder Qty" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((sku, idx) => (
                <React.Fragment key={sku.product}>
                  <TableRow className={`hover:bg-slate-50 ${expandedSku === sku.product ? "bg-blue-50/50" : ""}`}>
                    <TableCell className="w-8">
                      <button onClick={() => setExpandedSku(expandedSku === sku.product ? null : sku.product)}
                        className="text-slate-400 hover:text-slate-600">
                        {expandedSku === sku.product ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium text-sm">{sku.product}</TableCell>
                    <TableCell className="font-bold text-sm">{sku.total_qty}</TableCell>
                    <TableCell className="text-sm text-emerald-700">{fmtINR(sku.total_revenue)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtINR(sku.mrp)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{sku.store_count}</TableCell>
                    <TableCell className={`font-bold text-sm ${sku.warehouse_stock <= 0 ? "text-red-600" : sku.warehouse_stock < 5 ? "text-amber-600" : "text-slate-800"}`}>
                      {sku.warehouse_stock}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{sku.store_stock}</TableCell>
                    <TableCell className="text-xs text-slate-600">{sku.daily_rate}/day</TableCell>
                    <TableCell className={`text-sm font-bold ${sku.days_of_stock <= 90 ? "text-red-600" : sku.days_of_stock <= 135 ? "text-amber-600" : "text-emerald-600"}`}>
                      {sku.days_of_stock >= 999 ? "∞" : `${sku.days_of_stock}d`}
                    </TableCell>
                    <TableCell>{urgencyBadge(sku.restock_urgency)}</TableCell>
                    <TableCell className="font-bold text-sm text-blue-700">{sku.suggested_reorder > 0 ? sku.suggested_reorder : "-"}</TableCell>
                  </TableRow>
                  {/* Expanded monthly breakdown */}
                  {expandedSku === sku.product && (
                    <TableRow>
                      <TableCell colSpan={11} className="bg-slate-50 p-0">
                        <div className="px-6 py-3">
                          <p className="text-xs font-bold text-slate-500 mb-2">Monthly Breakdown — {sku.product}</p>
                          <div className="grid grid-cols-6 gap-2">
                            {(data?.months || []).map(m => {
                              const md = sku.monthly?.[m] || {};
                              const monthNames = {"01":"Jan","02":"Feb","03":"Mar","04":"Apr","05":"May","06":"Jun","07":"Jul","08":"Aug","09":"Sep","10":"Oct","11":"Nov","12":"Dec"};
                              const parts = m.split("-");
                              const label = `${monthNames[parts[1]] || parts[1]} ${parts[0]?.slice(2)}`;
                              return (
                                <div key={m} className={`rounded-lg border p-2.5 text-center ${md.qty ? "bg-white" : "bg-slate-100 opacity-50"}`}>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase">{label}</p>
                                  <p className="text-lg font-bold text-slate-800">{md.qty || 0}</p>
                                  <p className="text-[10px] text-emerald-600">{md.revenue ? fmtINR(md.revenue) : "-"}</p>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex gap-4 mt-2 text-xs text-slate-400">
                            <span>First sale: {sku.first_sale || "-"}</span>
                            <span>Last sale: {sku.last_sale || "-"}</span>
                            <span>Transactions: {sku.txn_count}</span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
        {filtered.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No SKU data found for the selected period.</div>}
      </div>
    </div>
  );
}



// ─── MAIN PAGE ──────────────────────────────────────────────
export default function SalesAnalytics() {
  const [mainTab, setMainTab] = useState("input"); // input | output
  const [outputTab, setOutputTab] = useState("stock"); // stock | sales | reorder | logic
  const [refreshKey, setRefreshKey] = useState(0);

  const handleUploadSuccess = () => setRefreshKey(k => k + 1);

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="sales-analytics-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Sales Analytics
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Upload store data and analyze stock & sales performance</p>
      </div>

      {/* Main Tabs: Input / Output */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit" data-testid="main-tabs">
        {[
          { id: "input", label: "Input", icon: Upload },
          { id: "output", label: "Output", icon: BarChart3 },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setMainTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mainTab === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Input Tab */}
      {mainTab === "input" && <InputTab onUploadSuccess={handleUploadSuccess} />}

      {/* Output Tab */}
      {mainTab === "output" && (
        <div>
          {/* Sub-tabs */}
          <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit" data-testid="output-tabs">
            {[
              { id: "stock", label: "Stock Position", icon: Package },
              { id: "sales", label: "Sales Position", icon: ShoppingCart },
              { id: "reorder", label: "Re-order Analytics", icon: RefreshCw },
              { id: "sku", label: "SKU Analytics", icon: Tag },
              { id: "logic", label: "Reorder Logic", icon: Settings },
            ].map(t => (
              <button
                key={t.id}
                data-testid={`output-tab-${t.id}`}
                onClick={() => setOutputTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  outputTab === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          {outputTab === "stock" && <StockPositionTab key={`stock-${refreshKey}`} />}
          {outputTab === "sales" && <SalesPositionTab key={`sales-${refreshKey}`} />}
          {outputTab === "reorder" && <ReorderAnalyticsTab key={`reorder-${refreshKey}`} />}
          {outputTab === "sku" && <SKUAnalyticsTab key={`sku-${refreshKey}`} apiBase={`${API}/sales-analytics`} />}
          {outputTab === "logic" && <ReorderLogicTab key={`logic-${refreshKey}`} />}
        </div>
      )}
    </div>
  );
}
